import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Shared format validators for the Agrícola aperturas flow. Empty values are
// always left to Validators.required so optional controls stay valid.

// Stricter than RFC 5322 on purpose: QA requires rejecting '#' in the local
// part, single-letter TLDs and consecutive/edge dots on either side of the @.
const EMAIL_PATTERN =
  /^[A-Za-z0-9_%+-]+(?:\.[A-Za-z0-9_%+-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

const DIGITS_ONLY_PATTERN = /^\d+$/;

export function agricolaEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return null;
    }
    return EMAIL_PATTERN.test(value) ? null : { invalidEmail: true };
  };
}

// Age of majority: below it a dependant gets a generated identification instead
// of asking for document type/number.
export const AGRICOLA_ADULT_AGE = 18;

// Identification type stored for generated (minor) documents: DUI.
export const AGRICOLA_MINOR_IDENTIFICATION_TYPE = 2;

// Normalizes the date shapes used across the flow (ISO, dd/MM/yyyy or a Date)
// to yyyy-MM-dd. Unparseable values are returned untouched.
export function agricolaToIsoDate(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const isoParts = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoParts) {
    return [isoParts[1], isoParts[2].padStart(2, '0'), isoParts[3].padStart(2, '0')].join('-');
  }

  const slashParts = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashParts) {
    return [slashParts[3], slashParts[2].padStart(2, '0'), slashParts[1].padStart(2, '0')].join('-');
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }

  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
}

// True when the birthday is under the age of majority as of today. Empty or
// unparseable birthdays are treated as adults so the document fields stay
// visible until a real date is entered.
export function agricolaIsMinor(birthday: unknown): boolean {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(agricolaToIsoDate(birthday));
  if (!parts) {
    return false;
  }

  const birth = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  const majority = new Date(birth.getFullYear() + AGRICOLA_ADULT_AGE, birth.getMonth(), birth.getDate());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return majority.getTime() > today.getTime();
}

// Identification generated for minors: holder document + minor correlative,
// e.g. 543455 + 1 -> 543455_1D.
export function agricolaMinorIdentificationId(holderIdentificationId: unknown, sequence: number): string {
  const holder = String(holderIdentificationId || '').trim();
  return holder ? `${holder}_${sequence}D` : '';
}

// Digits only: rejects decimals (55649.00), exponentials (55649e-3), negatives
// and any letter or symbol. Leading zeros are kept, they are significant in
// account and document numbers.
export function agricolaPositiveIntegerValidator(errorKey = 'invalidInteger'): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return null;
    }
    return DIGITS_ONLY_PATTERN.test(value) ? null : { [errorKey]: true };
  };
}
